import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, GripVertical, Pin, PinOff, X } from "lucide-react";
import {
  PLAYLIST_MAX_VIDEOS,
  PIN_AT_SEND_CATEGORIES,
  PLAYLIST_VISIBILITIES,
  VIEWER_ROLES,
  type PlaylistVisibility,
  type VideoCategory,
  type ViewerRole,
} from "@demore/shared/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select } from "@/components/ui/select";
import { api } from "@/lib/api";
import { cn, formatDuration } from "@/lib/utils";

type VideoRow = {
  id: string;
  title: string;
  category: VideoCategory;
  accessTier: string;
  durationSeconds: number | null;
  thumbnailUrl: string | null;
  status: string;
};

type PlaylistItem = {
  videoId: string;
  title: string;
  category: VideoCategory;
  durationSeconds: number | null;
  pinnedVersionId: string | null;
  currentVersionId?: string | null;
};

type ExistingPlaylistItem = PlaylistItem & {
  id: string;
  sortOrder: number;
  accessTier: string;
  status: string;
  hasPlayback: boolean;
  thumbnailUrl: string | null;
};

type SourceTab = "search" | "favorites" | "recents" | "promoted";

export function PlaylistBuilderPage() {
  const { playlistId } = useParams<{ playlistId: string }>();
  const isEdit = !!playlistId;
  const nav = useNavigate();
  const qc = useQueryClient();

  const [meta, setMeta] = useState({
    name: "",
    description: "",
    type: "custom" as "template" | "custom",
    visibility: "private" as PlaylistVisibility,
    roles: [] as ViewerRole[],
    publicSlug: "" as string,
  });
  const [items, setItems] = useState<PlaylistItem[]>([]);
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState<SourceTab>("search");
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  const { data: existing } = useQuery({
    queryKey: ["playlist", playlistId],
    queryFn: () => api.get<{
      playlist: {
        name: string;
        description: string | null;
        type: "template" | "custom";
        // CEO Rev 1.0: a campaign-scoped template carries its campaignId so the
        // video picker can be scoped to that campaign (no cross-campaign videos).
        campaignId?: string | null;
        visibility?: PlaylistVisibility;
        roles?: ViewerRole[];
        publicSlug?: string | null;
      };
      items: ExistingPlaylistItem[];
    }>(`/api/admin/playlists/${playlistId}`),
    enabled: isEdit,
  });

  // CEO Rev 1.0: scope the video picker to the template's campaign. This fixes
  // the workspace "Edit videos" regression — an unscoped picker 400'd for a
  // campaign_owner and let others pull ANOTHER campaign's videos into a scoped
  // template. In edit mode we wait for the playlist to load so the very first
  // fetch is already scoped.
  const scopedCampaignId = existing?.playlist.campaignId ?? null;
  const { data: library } = useQuery({
    queryKey: ["library-tabbed", tab, scopedCampaignId],
    queryFn: () => {
      const params = new URLSearchParams({ status: "approved" });
      if (tab === "favorites") params.set("favoritesOnly", "true");
      if (tab === "recents") params.set("recents", "true");
      if (tab === "promoted") params.set("promoted", "true");
      if (scopedCampaignId) params.set("campaignId", scopedCampaignId);
      return api.get<{ items: VideoRow[] }>(`/api/admin/videos?${params}`);
    },
    // Don't fire an unscoped fetch before the campaign-scoped template loads.
    enabled: !isEdit || existing !== undefined,
  });

  useEffect(() => {
    if (existing) {
      setMeta({
        name: existing.playlist.name,
        description: existing.playlist.description ?? "",
        type: existing.playlist.type,
        visibility: (existing.playlist.visibility ?? "private") as PlaylistVisibility,
        roles: (existing.playlist.roles ?? []) as ViewerRole[],
        publicSlug: existing.playlist.publicSlug ?? "",
      });
      setItems(existing.items.map((i) => ({
        videoId: i.videoId,
        title: i.title,
        category: i.category,
        durationSeconds: i.durationSeconds ?? null,
        pinnedVersionId: i.pinnedVersionId ?? null,
      })));
    }
  }, [existing]);

  const totalSec = useMemo(() => items.reduce((s, i) => s + (i.durationSeconds ?? 0), 0), [items]);
  const filteredLib = (library?.items ?? [])
    .filter((v) => v.title.toLowerCase().includes(filter.toLowerCase()) && !items.find((it) => it.videoId === v.id));

  function addItem(v: VideoRow) {
    if (items.length >= PLAYLIST_MAX_VIDEOS) {
      toast.error(`Max ${PLAYLIST_MAX_VIDEOS} videos`, { id: "playlist-max-videos" });
      return;
    }
    setItems((it) => [...it, {
      videoId: v.id,
      title: v.title,
      category: v.category,
      durationSeconds: v.durationSeconds,
      // Default pin behaviour per spec §4: performance_report pins to send-time;
      // evergreen follows current. Reflected here so the toggle starts correctly.
      pinnedVersionId: null,
    }]);
  }
  function move(idx: number, delta: number) {
    const next = [...items];
    const j = idx + delta;
    if (j < 0 || j >= next.length) return;
    const current = next[idx];
    const target = next[j];
    if (!current || !target) return;
    [next[idx], next[j]] = [target, current];
    setItems(next);
  }
  function remove(idx: number) {
    setItems((it) => it.filter((_, i) => i !== idx));
  }
  function togglePin(idx: number) {
    // Toggling here flips between "follow current version" (null) and
    // "pinned to current version at send time" (set on send). The actual
    // pinning to a specific version id happens server-side at issuance time;
    // here we record intent.
    setItems((it) => it.map((x, i) => i === idx ? { ...x, pinnedVersionId: x.pinnedVersionId ? null : "pin-at-send" } : x));
  }

  // Native HTML5 drag-and-drop ordering.
  function onDragStart(idx: number) { setDragIdx(idx); }
  function onDragOver(idx: number, e: React.DragEvent) {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setItems((cur) => {
      const next = [...cur];
      const [moved] = next.splice(dragIdx, 1);
      if (!moved) return cur;
      next.splice(idx, 0, moved);
      setDragIdx(idx);
      return next;
    });
  }
  function onDragEnd() { setDragIdx(null); }

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: meta.name,
        description: meta.description || undefined,
        type: meta.type,
        visibility: meta.visibility,
        roles: meta.roles,
        publicSlug:
          meta.visibility === "public" && meta.publicSlug
            ? meta.publicSlug
            : null,
        items: items.map((it, idx) => ({
          videoId: it.videoId,
          sortOrder: idx,
          // Send the *value* the row currently holds. "pin-at-send" is a
          // sentinel — the server treats anything non-null as "pin this
          // playlist row at send time"; the actual version id is resolved
          // there when the issuance is created.
          pinnedVersionId: it.pinnedVersionId || null,
        })),
      };
      return isEdit
        ? api.patch(`/api/admin/playlists/${playlistId}`, payload)
        : api.post("/api/admin/playlists", payload);
    },
    onSuccess: () => {
      toast.success("Playlist saved");
      qc.invalidateQueries({ queryKey: ["playlists"] });
      // Bug 5: bust the specific playlist's cache so reopening Edit shows the
      // saved pin/order instead of stale data.
      if (playlistId) qc.invalidateQueries({ queryKey: ["playlist", playlistId] });
      nav("/admin/playlists");
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Save failed"),
  });

  return (
    <div className="space-y-4 max-w-7xl">
      <div className="flex items-center justify-between">
        <h1 className="font-head text-2xl font-semibold">{isEdit ? "Edit playlist" : "New playlist"}</h1>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => nav("/admin/playlists")}>Cancel</Button>
          <Button disabled={!meta.name || items.length === 0 || save.isPending} onClick={() => save.mutate()}>{save.isPending ? "Saving…" : "Save playlist"}</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: library (with tabs) */}
        <Card>
          <CardHeader>
            <div className="flex gap-1 -mb-2">
              {(["search","favorites","recents","promoted"] as SourceTab[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={cn(
                    "px-2.5 py-1 text-xs font-medium rounded-md capitalize",
                    tab === t ? "bg-gold text-navy" : "text-muted-foreground hover:bg-foreground/5",
                  )}
                >
                  {t}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            <Input placeholder="Filter by title" value={filter} onChange={(e) => setFilter(e.target.value)} className="mb-3" />
            <ul className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              {filteredLib.map((v) => (
                <li key={v.id} className="flex items-center gap-3 border border-admin-rule rounded-md p-2">
                  <div className="w-16 aspect-video bg-navy-700 rounded overflow-hidden">
                    {v.thumbnailUrl && <img src={v.thumbnailUrl} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm line-clamp-1">{v.title}</div>
                    <div className="flex gap-1.5 mt-1">
                      <Badge variant="outline">{v.category.replace(/_/g, " ")}</Badge>
                      <Badge variant="muted">{v.accessTier.replace(/_/g, " ")}</Badge>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => addItem(v)}>Add</Button>
                </li>
              ))}
              {filteredLib.length === 0 && (
                <li className="text-sm text-muted-foreground text-center py-6">
                  {tab === "search" ? "No matching videos" :
                   tab === "favorites" ? "You haven't favorited any videos yet" :
                   tab === "recents" ? "No videos have been added to playlists yet" :
                   "No promoted videos"}
                </li>
              )}
            </ul>
          </CardContent>
        </Card>

        {/* Right: current playlist */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Playlist ({items.length}/{PLAYLIST_MAX_VIDEOS})</CardTitle>
              <span className="text-xs text-muted-foreground">Runtime: {formatDuration(totalSec)}</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={meta.name} onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={meta.type} onChange={(e) => setMeta((m) => ({ ...m, type: e.target.value as "template" | "custom" }))}>
                  <option value="custom">Custom</option>
                  <option value="template">Template (reusable)</option>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea value={meta.description} onChange={(e) => setMeta((m) => ({ ...m, description: e.target.value }))} />
            </div>
            {/* Audit V1: visibility + role tags + public slug */}
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-admin-rule">
              <div className="space-y-1.5">
                <Label>Visibility</Label>
                <Select
                  value={meta.visibility}
                  onChange={(e) =>
                    setMeta((m) => ({
                      ...m,
                      visibility: e.target.value as PlaylistVisibility,
                    }))
                  }
                >
                  {PLAYLIST_VISIBILITIES.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </Select>
              </div>
              {meta.visibility === "public" && (
                <div className="space-y-1.5">
                  <Label>Public slug</Label>
                  <Input
                    value={meta.publicSlug}
                    onChange={(e) =>
                      setMeta((m) => ({ ...m, publicSlug: e.target.value }))
                    }
                    placeholder="lowercase-with-hyphens"
                  />
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Onboarding roles (tag for suggestion when composing)</Label>
              <div className="flex flex-wrap gap-2">
                {VIEWER_ROLES.map((r) => {
                  const on = meta.roles.includes(r);
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() =>
                        setMeta((m) => ({
                          ...m,
                          roles: on
                            ? m.roles.filter((x) => x !== r)
                            : [...m.roles, r],
                        }))
                      }
                      className={cn(
                        "px-2 py-1 text-xs rounded-md border",
                        on
                          ? "bg-gold text-navy border-gold"
                          : "text-muted-foreground border-admin-rule hover:bg-foreground/5",
                      )}
                    >
                      {r}
                    </button>
                  );
                })}
              </div>
            </div>
            <ul className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
              {items.map((it, idx) => {
                const categoryDefaultPin = PIN_AT_SEND_CATEGORIES.includes(it.category);
                const pinned = !!it.pinnedVersionId || categoryDefaultPin;
                return (
                  <li
                    key={it.videoId}
                    draggable
                    onDragStart={() => onDragStart(idx)}
                    onDragOver={(e) => onDragOver(idx, e)}
                    onDragEnd={onDragEnd}
                    className={cn(
                      "flex items-center gap-2 border border-admin-rule rounded-md p-2 bg-admin-panel",
                      dragIdx === idx && "opacity-50",
                    )}
                  >
                    <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab shrink-0" />
                    <span className="text-xs text-muted-foreground w-5 text-center">{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm line-clamp-1">{it.title}</div>
                      <div className="text-xs text-muted-foreground">{it.category.replace(/_/g, " ")} · {formatDuration(it.durationSeconds)}</div>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => togglePin(idx)}
                      title={pinned
                        ? (categoryDefaultPin ? "Pinned (category default — performance_report locks at send)" : "Pinned: locks to version at send time")
                        : "Following current version (re-uploads auto-apply)"}
                    >
                      {pinned ? <Pin className="h-4 w-4 text-gold" /> : <PinOff className="h-4 w-4 text-muted-foreground" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0}><ChevronUp className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => move(idx, +1)} disabled={idx === items.length - 1}><ChevronDown className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(idx)}><X className="h-4 w-4" /></Button>
                  </li>
                );
              })}
              {items.length === 0 && (
                <li className="text-sm text-muted-foreground text-center py-6">
                  Add videos from the library to build your playlist. Drag rows to reorder, click the pin icon to lock a row to its current version.
                </li>
              )}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
